/**
 * Deep Merge Utility
 * Merges nested objects with specific rules:
 * - Primitives: source overwrites target
 * - Objects: recursively merge
 * - Arrays: replace entirely (no array merging)
 */

/**
 * Deep merge source into target
 * Returns a frozen, immutable copy
 */
export function deepMerge<T>(target: T, source: Partial<T>): T {
  if (source === null || source === undefined) {
    return Object.freeze({ ...target });
  }

  const result = { ...target };

  for (const key in source) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }

    const sourceValue = source[key];
    const targetValue = result[key];

    // Arrays: replace entirely
    if (Array.isArray(sourceValue)) {
      (result as Record<string, unknown>)[key] = [...sourceValue];
    }
    // Objects: recursively merge
    else if (
      sourceValue !== null &&
      typeof sourceValue === "object" &&
      !Array.isArray(sourceValue) &&
      targetValue !== null &&
      typeof targetValue === "object" &&
      !Array.isArray(targetValue)
    ) {
      (result as Record<string, unknown>)[key] = deepMerge(targetValue, sourceValue as Partial<typeof targetValue>);
    }
    // Primitives: source overwrites target
    else {
      (result as Record<string, unknown>)[key] = sourceValue;
    }
  }

  return Object.freeze(result);
}
